import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

const AutoScaleText = ({
  children,
  className = undefined,
  style = undefined,
  minFontSize = 10,
  maxFontSize = 36,
  widthOnly = false,
}) => {
  const textRef = useRef(null);
  const [fontSize, setFontSize] = useState(maxFontSize);

  const resize = useCallback(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }

    if (element.clientWidth === 0) {
      return;
    }

    const fits = (size) => {
      element.style.fontSize = `${size}px`;

      const fitsWidth = element.scrollWidth <= element.clientWidth + 1;
      if (widthOnly) {
        return fitsWidth;
      }

      const fitsHeight = element.scrollHeight <= element.clientHeight + 1;
      return fitsWidth && fitsHeight;
    };

    let low = minFontSize;
    let high = maxFontSize;
    let best = minFontSize;

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (fits(mid)) {
        best = mid;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    setFontSize(best);
  }, [maxFontSize, minFontSize, widthOnly]);

  useLayoutEffect(() => {
    resize();

    if (!window.ResizeObserver) {
      return undefined;
    }

    const parent = textRef.current?.parentElement;
    if (!parent) {
      return undefined;
    }

    let rafId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => resize());
    });
    observer.observe(parent);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [children, resize]);

  return (
    <div
      ref={textRef}
      className={className}
      style={{
        ...style,
        fontSize: `${fontSize}px`,
        width: "100%",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </div>
  );
};

export default AutoScaleText;
